import { assertNoXKeyCollision } from './aggregate';
import { assertRowsMatchFixtures } from './dimensions';
import { assertWeightTablesMatchFixtures, generateRows, type MentionRow } from './generator';
import { buildRowIndex } from './rowIndex';

/**
 * The corpus, generated once at module load.
 *
 * Module scope, not a provider, not an effect: there is no loading state, no
 * fetch, and nothing to invalidate. ~4,000 rows take about a millisecond to
 * build, which is cheaper than the ceremony of making it async would be.
 *
 * `MentionRow` is declared in `generator.ts` and re-exported here rather than
 * the other way round. The plan sketched it in this file, but the generator is
 * what constructs the shape, and declaring it here would make the two modules
 * import each other.
 */
export type { MentionRow };

/**
 * Change this and every figure in the app changes — consistently, because one
 * seed drives the whole pass. It is the only knob that alters the corpus.
 */
export const SEED = 0x5eed_1234;

/*
 * Both invariants run before anything reads the rows, so a fixture edit fails at
 * boot with a message naming the offending value rather than drawing an empty
 * chart three screens later. The weight-table check comes first: it catches the
 * cause, while the row check catches the symptom.
 */
assertWeightTablesMatchFixtures();

/*
 * And that no category value can shadow the key every chart row keeps its own
 * category on. See `X_KEY` — this one was found the hard way.
 */
assertNoXKeyCollision();

export const allRows: readonly MentionRow[] = generateRows(SEED);

assertRowsMatchFixtures(allRows);

/**
 * The date index over the corpus — also module scope, also built once.
 *
 * `generateRows` already emits rows in ascending day order, but `buildRowIndex`
 * sorts anyway: relying on the generator's loop order would make the binary
 * search silently wrong the day someone reorders that loop.
 */
export const rowIndex = buildRowIndex(allRows);
